import { Component, OnInit, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ValidateNotEmptyInput } from 'src/app/shared/validators/is-not-empty.validator';
import { HttpErrorResponse } from '@angular/common/http';
import { PasswordService } from 'src/app/shared/services/password.service';
import { ChangePassword } from 'src/app/shared/interfaces/change-password.interface';
import { ServiceResponse } from 'src/app/shared/models/serviceResponse.model';
import { UIIconsEnum } from 'src/app/shared/enums/icons.enum';


declare var bootstrap: any;

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
})
export class ChangePasswordComponent implements OnInit, AfterViewInit {
  changePasswordForm!: FormGroup;
  showSuccess: boolean = false;
  errorMessage: string = '';
  showError: boolean = false;
  redirectCountdown: number = 3;

  // Password visibility toggles
  showCurrentPassword: boolean = false;
  showNewPassword: boolean = false;
  showConfirmPassword: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private passwordService: PasswordService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit() {
    this.changePasswordForm = this.formBuilder.group({
      currentPassword: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(50),
          ValidateNotEmptyInput,
          this.noSpacesValidator
        ],
      ],
      newPassword: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(50),
          ValidateNotEmptyInput,
          Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/),
          this.noSpacesValidator
        ],
      ],
      confirmNewPassword: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(50),
          ValidateNotEmptyInput,
          this.noSpacesValidator
        ],
      ],
    });

    // Add value change listeners to trigger password match validation
    this.changePasswordForm.get('newPassword')?.valueChanges.subscribe(() => {
      this.changePasswordForm.get('confirmNewPassword')?.updateValueAndValidity();
    });

    this.changePasswordForm.get('confirmNewPassword')?.setValidators([
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(32),
      ValidateNotEmptyInput,
      this.noSpacesValidator,
      this.passwordMatchValidator.bind(this)
    ]);
  }

  ngAfterViewInit(): void {
    // Initialize tooltips
    const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    tooltipTriggerList.map(function (tooltipTriggerEl) {
      return new bootstrap.Tooltip(tooltipTriggerEl);
    });
  }

  // Toggle password visibility methods
  toggleCurrentPasswordVisibility(): void {
    this.showCurrentPassword = !this.showCurrentPassword;
  }

  toggleNewPasswordVisibility(): void {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  // Password validation helper methods
  hasMinLength(): boolean {
    const password = this.changePasswordForm.get('newPassword')?.value;
    return password?.length >= 8;
  }

  hasUpperCase(): boolean {
    const password = this.changePasswordForm.get('newPassword')?.value;
    return /[A-Z]/.test(password || '');
  }

  hasLowerCase(): boolean {
    const password = this.changePasswordForm.get('newPassword')?.value;
    return /[a-z]/.test(password || '');
  }

  hasNumber(): boolean {
    const password = this.changePasswordForm.get('newPassword')?.value;
    return /[0-9]/.test(password || '');
  }

  hasSpecialChar(): boolean {
    const password = this.changePasswordForm.get('newPassword')?.value;
    return /[!@#$%^&*(),.?":{}|<>]/.test(password || '');
  }

  hasSpaces(): boolean {
    const password = this.changePasswordForm.get('newPassword')?.value;
    return /\s/.test(password || '');
  }

  noSpacesValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const hasSpaces = /\s/.test(control.value);
    return hasSpaces ? { 'containsSpaces': true } : null;
  }


  getVisibilityIcon(isHidden: boolean) {
    return isHidden == true ? UIIconsEnum.Show : UIIconsEnum.Hide;
  }

  passwordMatchValidator(control: any): { [key: string]: boolean } | null {
    if (!this.changePasswordForm) {
      return null;
    }

    const newPassword = this.changePasswordForm.get('newPassword')?.value;
    const confirmNewPassword = control.value;

    if (newPassword !== confirmNewPassword) {
      return { 'mismatch': true };
    }

    return null;
  }

  changePassword() {
    // Reset status flags
    this.showSuccess = false;
    this.showError = false;
    this.errorMessage = '';
    this.redirectCountdown = 5;

    if (this.changePasswordForm.valid) {
      const changePasswordRequest: ChangePassword = {
        currentPassword: this.changePasswordForm.get('currentPassword')?.value,
        newPassword: this.changePasswordForm.get('newPassword')?.value,
        confirmNewPassword:
          this.changePasswordForm.get('confirmNewPassword')?.value,
      };

      this.passwordService.changePassword(changePasswordRequest).subscribe({
        next: (response: ServiceResponse<any>) => {
          if (response.isSuccess) {
            this.showSuccess = true;
            // Reset form after successful password change
            this.changePasswordForm.reset();
            // Start countdown and redirect
            const countdownInterval = setInterval(() => {
              this.redirectCountdown--;
              if (this.redirectCountdown <= 0) {
                clearInterval(countdownInterval);
                this.router.navigate(['/']);
              }
            }, 1000);
          } else {
            this.showError = true;
            // Join all error messages from the response
            if (response.messages && response.messages.length > 0) {
              this.errorMessage = response.messages
                .map(msg => msg.content)
                .filter(content => content) // Filter out undefined/null messages
                .join(', ');
            } else {
              this.errorMessage = 'An error occurred while changing your password.';
            }
          }
        },
        error: (err: HttpErrorResponse) => {
          this.showError = true;
          if (err.error && err.error.messages && err.error.messages.length > 0) {
            // Handle structured error response
            this.errorMessage = err.error.messages
              .map((msg: any) => msg.content)
              .filter((content: string | undefined) => content)
              .join(', ');
          } else if (err.error && typeof err.error === 'string') {
            // Handle string error only code of error
            this.errorMessage = 'Something went wrong, please try again later.';
          } else {
            // Fallback error message
            this.errorMessage = err.message || 'An error occurred while changing your password.';
          }
        },
      });
    }
  }

  hasError(controlName: string, errorName: string) {
    return this.changePasswordForm.controls[controlName].hasError(errorName);
  }
}
